import { ENV } from '@/constants';

import { BtcLikeNetwork, BtcLikeNetworkParams, NetworkNet } from '../common';

import { getSatPerVbEstimateFee } from './api';

/**
 * The current default minimum relay fee is 1 sat/vbyte.
 * This is the default policy and can change anytime!!!
 * IMPORTANT: Lower than 1 sat leads to txs not being relayed with a 400 error in eg. XVerse
 * @see https://learnmeabitcoin.com/technical/transaction/fee/#:~:text=The%20current%20default%20minimum%20relay,get%20mined%20into%20the%20blockchain
 */
export const BTC_MIN_RELAY_TX_FEE_SAT_PER_VBYTE = 1;

export const BTC_MAIN_NET: BtcLikeNetworkParams = {
  bech32: 'bc',
  pubKeyHash: 0x00,
  scriptHash: 0x05,
  wif: 0x80,
};

export const BTC_TEST_NET: BtcLikeNetworkParams = {
  bech32: 'tb',
  pubKeyHash: 0x6f,
  scriptHash: 0xc4,
  wif: 0xef,
};

export const BtcNetworkSettings = {
  [NetworkNet.Mainnet]: BTC_MAIN_NET,
  [NetworkNet.Testnet]: BTC_TEST_NET,
};

const BtcExplorerMap: Record<NetworkNet, string> = {
  [NetworkNet.Mainnet]: 'https://mempool.space',
  [NetworkNet.Testnet]: 'https://mempool.space/testnet',
};

const isBtcNetwork = (value: string): value is NetworkNet => (Object.values(NetworkNet) as string[]).includes(value);
const mempoolApiEndpoint = (ENV['VITE_BTC_MEMPOOL_API_ENDPOINT'] as string)
  .split('/')
  .map((slug) => (slug[0] === '$' ? (ENV[slug.slice(1)] as string) : slug))
  .join('/');

export const NETWORK: BtcLikeNetwork = {
  get Network() {
    if (!isBtcNetwork(ENV['VITE_BTC_NETWORK'])) {
      throw new Error(
        'Incorrect value ' +
          ENV['VITE_BTC_NETWORK'] +
          ' for variable VITE_BTC_NETWORK. Use one of ' +
          Object.values(NetworkNet).join(', ')
      );
    }

    return ENV['VITE_BTC_NETWORK'];
  },

  get BlockstreamApiEndpoint() {
    return (ENV['VITE_BTC_BLOCKSTREAM_API_ENDPOINT'] as string)
      .split('/')
      .map((slug) => (slug[0] === '$' ? (ENV[slug.slice(1)] as string) : slug))
      .join('/');
  },

  get MempoolApiEndpoint() {
    return mempoolApiEndpoint;
  },

  get Settings() {
    return BtcNetworkSettings[NETWORK.Network];
  },

  get MempoolSpendingUtxoEndpoint(): string {
    return (ENV['VITE_BTC_SPENDING_UTXO_ENDPOINT'] as string)
      .split('/')
      .map((slug) => (slug[0] === '$' ? (ENV[slug.slice(1)] as string) : slug))
      .join('/');
  },

  get ExplorerUrl() {
    const network = this.Network;

    return BtcExplorerMap[network];
  },

  get DustLimit() {
    return Number(ENV['VITE_BTC_DUST_LIMIT']);
  },

  get FeeCorrection() {
    return BigInt(Number(import.meta.env['VITE_BTC_FEE_CORRECTION'] || 1));
  },

  api: {
    getSatPerVbEstimateFee: () => getSatPerVbEstimateFee(mempoolApiEndpoint),
  },
};

export const CONNECTOR_ID_FIELD = 'current-connector-id';
